import _ from 'underscore';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import { INVALID_URL_ERROR, ZOOM_HOST_ERROR } from 'bundles/author-group-events/constants/eventConstants';

const SERIALIZED_PROPS: Array<keyof SerializedState> = [
  'events',
  'eventSeriesById',
  'handledError',
  'handledError',
  'defaultEventLink',
];

type SerializedState = {
  saveInProgress: boolean | undefined;
  zoomErrorMessage: string | null | undefined;
  events: any;
  eventSeriesById: Record<string, any> | undefined;
  defaultEventLink: null | undefined;
  handledError: null | undefined | string;
  unhandledError: any;
};

const ZOOM_ERROR_CODES = ['1113', '1114', '1115'];

class GroupEventsStore extends BaseStore {
  static storeName = 'GroupEventsStore';

  declare saveInProgress: boolean | undefined;

  declare zoomErrorMessage: string | null | undefined;

  declare events: any;

  declare eventSeriesById: Record<string, any> | undefined;

  declare defaultEventLink: null | undefined;

  declare handledError: null | undefined | string;

  declare unhandledError: any;

  static handlers = {
    RECEIVED_GROUP_EVENTS: 'receivedGroupEvents',
    RECEIVED_DEFAULT_EVENT_LINK: 'receivedDefaultEventLink',
    CREATED_GROUP_EVENT: 'createdGroupEvent',
    UPDATED_GROUP_EVENT: 'updatedGroupEvent',
    DELETED_GROUP_EVENT: 'deletedGroupEvent',
    RECEIVED_GROUP_EVENT_SERIES: 'receivedGroupEventSeries',
    CREATED_GROUP_EVENT_SERIES: 'createdGroupEventSeries',
    DELETED_GROUP_EVENT_SERIES: 'deletedGroupEventSeries',
    GROUP_EVENT_FAIL: 'groupEventFail',
    GROUP_EVENT_CLEAR_ERROR: 'groupEventClearError',
    GROUP_EVENT_SAVING_IN_PROGRESS: 'groupEventSavingInProgress',
  };

  constructor(dispatcher: $TSFixMe) {
    super(dispatcher);
    this.reset();
  }

  dehydrate() {
    return _(this).pick(...SERIALIZED_PROPS);
  }

  rehydrate(state: $TSFixMe) {
    Object.assign(this, _(state).pick(...SERIALIZED_PROPS));
  }

  reset() {
    this.events = null;
    this.eventSeriesById = {};
    this.defaultEventLink = null;
    this.resetErrors();
  }

  resetErrors() {
    this.handledError = null;
    this.unhandledError = null;
    this.zoomErrorMessage = null;
  }

  hasLoaded() {
    return !!this.events;
  }

  getEvents() {
    return this.events;
  }

  getEventSeries(eventSeriesId: $TSFixMe) {
    return this.eventSeriesById?.[eventSeriesId];
  }

  getDefaultEventLink() {
    return this.defaultEventLink;
  }

  getUnhandledError() {
    return this.unhandledError;
  }

  getHandledError() {
    return this.handledError;
  }

  getZoomErrorMessage() {
    return this.zoomErrorMessage;
  }

  getSaveInProgress() {
    return this.saveInProgress;
  }

  receivedGroupEvents({ events }: $TSFixMe) {
    this.events = events;
    this.emitChange();
  }

  receivedDefaultEventLink({ defaultEventLink }: $TSFixMe) {
    this.defaultEventLink = defaultEventLink;
    this.emitChange();
  }

  createdGroupEvent({ event }: $TSFixMe) {
    this.saveInProgress = false;
    this.resetErrors();
    this.events.add(event);
    this.emitChange();
  }

  updatedGroupEvent({ event }: $TSFixMe) {
    this.saveInProgress = false;
    this.resetErrors();
    this.events.update(event);
    this.emitChange();
  }

  deletedGroupEvent({ event }: $TSFixMe) {
    this.saveInProgress = false;
    this.resetErrors();
    this.events.remove(event);
    this.emitChange();
  }

  receivedGroupEventSeries({ eventSeries }: { eventSeries: { id: string } }) {
    this.saveInProgress = false;
    if (this.eventSeriesById) {
      this.eventSeriesById[eventSeries.id] = eventSeries;
    }
    this.events.updateEventSeries(eventSeries);
    this.emitChange();
  }

  createdGroupEventSeries({ eventSeries, events }: $TSFixMe) {
    this.saveInProgress = false;
    this.resetErrors();

    if (this.eventSeriesById) {
      this.eventSeriesById[eventSeries.id] = eventSeries;
    }
    events.forEach((event: $TSFixMe) => {
      this.events.add(event);
    });
    this.events = this.events.sortByStartDate();
    this.emitChange();
  }

  deletedGroupEventSeries({ eventSeriesId }: $TSFixMe) {
    this.saveInProgress = false;
    this.resetErrors();

    if (this.eventSeriesById) {
      this.eventSeriesById[eventSeriesId] = null;
    }
    this.emitChange();
  }

  groupEventFail({ error }: $TSFixMe) {
    this.saveInProgress = false;
    this.resetErrors();
    const { message: zoomErrorMessage } = error;

    const errorString = JSON.stringify(error);
    if (
      // note: the first case should catch this error, but being extra cautious since it seems brittle
      errorString.indexOf('\\\\\\"code\\\\\\":1001,') > -1 ||
      errorString.indexOf('User does not exist') > -1 ||
      errorString.indexOf('User not exist') > -1 ||
      ZOOM_ERROR_CODES.includes(error.errorCode)
    ) {
      this.handledError = ZOOM_HOST_ERROR;
      this.zoomErrorMessage = zoomErrorMessage;
    } else if (errorString.indexOf('Invalid URL:') > -1) {
      this.handledError = INVALID_URL_ERROR;
    } else {
      this.unhandledError = error;
    }
    this.emitChange();
  }

  groupEventClearError() {
    this.handledError = null;
    this.zoomErrorMessage = null;
    this.unhandledError = null;
    this.emitChange();
  }

  groupEventSavingInProgress() {
    this.saveInProgress = true;
    this.emitChange();
  }
}

export default GroupEventsStore;
